import {
  ChevronDown,
  ChevronUp,
  Eye,
  Filter,
  Pencil,
  Search,
  Trash2,
  X,
} from 'lucide-react'
import { useEffect, useMemo, useState } from 'react'
import { useSearchParams } from 'react-router-dom'

import { useSales } from '@/modules/admin/hooks/useSales'
import EditDCRModal from '@/modules/dcr/components/EditDCRModal'
import { useDCRStore } from '@/modules/dcr/store/dcr.store'
import { ConfirmDialog } from '@/shared/components/ui/ConfirmDialog'
import CustomDateRangePicker from '@/shared/components/ui/CustomDateRangePicker'
import DashboardPeriodFilter from '@/shared/components/ui/DashboardPeriodFilter'

export default function AdminDCRPage() {
  const [search, setSearch] = useState('')
  const [isEditOpen, setIsEditOpen] = useState(false)
  const [selectedDCR, setSelectedDCR] = useState(null)
  const [selectedViewDCR, setSelectedViewDCR] = useState(null)
  const [dcrToDelete, setDCRToDelete] = useState(null)
  const [isDeleteOpen, setIsDeleteOpen] = useState(false)
  const [isDeleting, setIsDeleting] = useState(false)

  const [searchParams, setSearchParams] = useSearchParams()
  const periodParam = searchParams.get('period') || 'all'
  const dateParam = searchParams.get('date') || ''
  const startDateParam = searchParams.get('startDate') || ''
  const endDateParam = searchParams.get('endDate') || ''
  const salesPersonParam = searchParams.get('salesPerson') || ''

  const [currentPage, setCurrentPage] = useState(1)
  const [selectedDate, setSelectedDate] = useState(dateParam)
  const [period, setPeriod] = useState(periodParam)
  const [salesPerson, setSalesPerson] = useState(salesPersonParam)
  const [isFilterOpen, setIsFilterOpen] = useState(false)
  const [customRange, setCustomRange] = useState({
    startDate: startDateParam,
    endDate: endDateParam,
  })

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setPeriod(periodParam)
    setSelectedDate(dateParam)
    setSalesPerson(salesPersonParam)
    setCustomRange({
      startDate: startDateParam,
      endDate: endDateParam,
    })
  }, [periodParam, dateParam, salesPersonParam, startDateParam, endDateParam])

  const { dcrs, isLoading, isSubmitting, fetchDCRs, updateDCR, deleteDCR } =
    useDCRStore()

  const { sales, fetchSales } = useSales()

  useEffect(() => {
    const timer = setTimeout(() => {
      fetchDCRs({
        search,
        period: selectedDate ? 'custom' : period,
        salesPerson: salesPerson || undefined,
        date: selectedDate || undefined,
        startDate: selectedDate
          ? selectedDate
          : period === 'custom'
            ? customRange.startDate
            : undefined,
        endDate: selectedDate
          ? selectedDate
          : period === 'custom'
            ? customRange.endDate
            : undefined,
      })

      setCurrentPage(1)
    }, 500)

    return () => clearTimeout(timer)
  }, [search, selectedDate, period, salesPerson, customRange, fetchDCRs])

  useEffect(() => {
    fetchSales({
      page: 1,
      limit: 100,
    })
  }, [fetchSales])

  const handleOpenView = (dcr) => {
    setSelectedViewDCR(dcr)
  }

  const handleCloseView = () => {
    setSelectedViewDCR(null)
  }

  const handleOpenEdit = (dcr) => {
    setSelectedDCR(dcr)
    setIsEditOpen(true)
  }

  const handleOpenDelete = (dcr) => {
    setDCRToDelete(dcr)
    setIsDeleteOpen(true)
  }

  const handleConfirmDelete = async () => {
    if (!dcrToDelete) return

    try {
      setIsDeleting(true)
      await deleteDCR(dcrToDelete._id)

      setIsDeleteOpen(false)
      setDCRToDelete(null)
    } catch {
      // Error is handled by the store
    } finally {
      setIsDeleting(false)
    }
  }

  const pageSize = 5

  const paginatedDCRs = useMemo(() => {
    const startIndex = (currentPage - 1) * pageSize
    const endIndex = startIndex + pageSize

    return dcrs.slice(startIndex, endIndex)
  }, [dcrs, currentPage])

  return (
    <div className="flex w-full min-w-0 flex-col gap-4 overflow-x-hidden sm:gap-6">
      {/* Header */}
      <div className="w-full rounded-3xl border bg-white p-5 shadow-sm sm:p-7">
        <div>
          <h1 className="text-3xl font-bold sm:text-4xl">DCR</h1>

          <p className="mt-2 text-sm text-muted-foreground sm:text-base">
            View customer visit records submitted by salespersons.
          </p>
        </div>
      </div>

      {/* Table */}
      <div className="w-full min-w-0 overflow-hidden rounded-3xl border bg-white shadow-sm">
        {/* Search */}
        <div className="border-b p-4 sm:p-5">
          <div className="flex flex-col gap-3 lg:flex-row lg:items-center">
            {/* Search */}
            <div className="flex w-full items-center gap-3 rounded-full bg-slate-100 px-5 py-3.5 lg:flex-1">
              <Search className="h-5 w-5 shrink-0 text-slate-500" />

              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search by customer, contact person, email or phone..."
                className="w-full bg-transparent text-sm text-slate-700 outline-none placeholder:text-slate-500"
              />
            </div>

            {/* Period Filter */}
            {/* Filters */}
            <div className="flex flex-wrap items-center gap-2">
              {/* Salesperson Filter */}
              <div className="relative">
                <button
                  type="button"
                  onClick={() => setIsFilterOpen((prev) => !prev)}
                  className="flex h-10 items-center gap-2 rounded-full border border-slate-200 bg-white px-4 text-xs font-semibold text-slate-700 shadow-sm hover:bg-slate-50"
                >
                  <Filter className="size-3.5 text-slate-500" />

                  {salesPerson
                    ? sales.find((person) => person._id === salesPerson)
                        ?.name || 'Salesperson'
                    : 'All Salespersons'}

                  {isFilterOpen ? (
                    <ChevronUp className="size-3 text-slate-400" />
                  ) : (
                    <ChevronDown className="size-3 text-slate-400" />
                  )}
                </button>

                {isFilterOpen && (
                  <div className="absolute right-0 top-full z-50 mt-1 max-h-60 w-52 overflow-y-auto rounded-xl border border-slate-100 bg-white py-1.5 shadow-xl">
                    <button
                      type="button"
                      onClick={() => {
                        setSalesPerson('')
                        setIsFilterOpen(false)
                        const updated = new URLSearchParams(searchParams)
                        updated.delete('salesPerson')
                        setSearchParams(updated)
                      }}
                      className={`w-full px-4 py-2.5 text-left text-xs font-medium hover:bg-slate-50 ${
                        !salesPerson
                          ? 'bg-slate-50 text-slate-900'
                          : 'text-slate-600'
                      }`}
                    >
                      All Salespersons
                    </button>

                    {sales.map((person) => (
                      <button
                        key={person._id}
                        type="button"
                        onClick={() => {
                          setSalesPerson(person._id)
                          setIsFilterOpen(false)
                          const updated = new URLSearchParams(searchParams)
                          updated.set('salesPerson', person._id)
                          setSearchParams(updated)
                        }}
                        className={`w-full px-4 py-2.5 text-left text-xs font-medium hover:bg-slate-50 ${
                          salesPerson === person._id
                            ? 'bg-slate-50 text-slate-900'
                            : 'text-slate-600'
                        }`}
                      >
                        {person.name}
                      </button>
                    ))}
                  </div>
                )}
              </div>

              {/* Date Input Filter */}
              <div className="flex items-center gap-2">
                <span className="text-xs font-medium text-slate-500">Date</span>
                <input
                  type="date"
                  value={selectedDate}
                  onChange={(e) => {
                    const val = e.target.value
                    setSelectedDate(val)
                    const updated = new URLSearchParams(searchParams)
                    if (val) {
                      setPeriod('custom')
                      updated.set('date', val)
                      updated.set('period', 'custom')
                    } else {
                      updated.delete('date')
                    }
                    setSearchParams(updated)
                  }}
                  className="h-10 rounded-full border border-slate-200 bg-white px-3.5 text-xs font-medium text-slate-700 shadow-sm outline-none transition focus:border-slate-400"
                />
                {selectedDate && (
                  <button
                    type="button"
                    onClick={() => {
                      setSelectedDate('')
                      setPeriod('all')
                      const updated = new URLSearchParams(searchParams)
                      updated.delete('date')
                      updated.delete('period')
                      updated.delete('startDate')
                      updated.delete('endDate')
                      setSearchParams(updated)
                    }}
                    className="text-xs text-slate-500 hover:text-slate-700 underline"
                  >
                    Clear
                  </button>
                )}
              </div>

              {/* Custom Date Range */}
              {period === 'custom' && !selectedDate && (
                <CustomDateRangePicker
                  startDate={customRange.startDate}
                  endDate={customRange.endDate}
                  onChange={(range) => {
                    setCustomRange(range)
                    const updated = new URLSearchParams(searchParams)
                    updated.set('period', 'custom')
                    if (range.startDate)
                      updated.set('startDate', range.startDate)
                    if (range.endDate) updated.set('endDate', range.endDate)
                    setSearchParams(updated)
                  }}
                />
              )}

              {/* Period Filter */}
              <DashboardPeriodFilter
                value={selectedDate ? 'custom' : period}
                onChange={(newPeriod) => {
                  setPeriod(newPeriod)
                  const updated = new URLSearchParams(searchParams)
                  if (newPeriod === 'all') {
                    updated.delete('period')
                    updated.delete('date')
                    updated.delete('startDate')
                    updated.delete('endDate')
                  } else {
                    updated.set('period', newPeriod)
                    if (newPeriod !== 'custom') {
                      setSelectedDate('')
                      updated.delete('date')
                      updated.delete('startDate')
                      updated.delete('endDate')
                    }
                  }
                  setSearchParams(updated)
                  if (newPeriod !== 'custom') {
                    setSelectedDate('')
                  }
                }}
              />
            </div>
          </div>
        </div>

        {/* Table */}
        <div className="overflow-x-auto">
          <table className="w-full min-w-[1250px]">
            <thead>
              <tr className="border-b text-left">
                <th className="px-5 py-4 text-sm font-semibold">Date</th>

                <th className="px-5 py-4 text-sm font-semibold">
                  Company Name
                </th>

                <th className="px-5 py-4 text-sm font-semibold">Salesperson</th>

                <th className="px-5 py-4 text-sm font-semibold">
                  Contact Person
                </th>

                <th className="px-5 py-4 text-sm font-semibold">
                  Phone Number
                </th>

                <th className="px-5 py-4 text-sm font-semibold">Email</th>

                <th className="px-5 py-4 text-sm font-semibold">Remarks</th>

                <th className="px-5 py-4 text-sm font-semibold">Actions</th>
              </tr>
            </thead>

            <tbody>
              {isLoading ? (
                <tr>
                  <td
                    colSpan={8}
                    className="px-5 py-10 text-center text-sm text-slate-500"
                  >
                    Loading DCRs...
                  </td>
                </tr>
              ) : dcrs.length === 0 ? (
                <tr>
                  <td
                    colSpan={8}
                    className="px-5 py-10 text-center text-sm text-slate-500"
                  >
                    No DCR records found.
                  </td>
                </tr>
              ) : (
                paginatedDCRs.map((dcr) => {
                  const calls = dcr.calls || []

                  // Extract values from calls or top-level DCR
                  const allCompanies =
                    calls.length > 0
                      ? calls.map((c) => c.customerName?.trim()).filter(Boolean)
                      : dcr.customerName
                        ? [dcr.customerName.trim()]
                        : []
                  const uniqueCompanies = [...new Set(allCompanies)]
                  const primaryCompany =
                    uniqueCompanies[0] || dcr.customerName || '-'
                  const extraCompanies = uniqueCompanies.length - 1

                  const allContacts =
                    calls.length > 0
                      ? calls
                          .map((c) => c.contactPersonName?.trim())
                          .filter(Boolean)
                      : dcr.contactPersonName
                        ? [dcr.contactPersonName.trim()]
                        : []
                  const uniqueContacts = [...new Set(allContacts)]
                  const primaryContact =
                    uniqueContacts[0] || dcr.contactPersonName || '-'
                  const extraContacts = uniqueContacts.length - 1

                  const allPhones =
                    calls.length > 0
                      ? calls.map((c) => c.phoneNumber?.trim()).filter(Boolean)
                      : dcr.phoneNumber
                        ? [dcr.phoneNumber.trim()]
                        : []
                  const uniquePhones = [...new Set(allPhones)]
                  const primaryPhone = uniquePhones[0] || dcr.phoneNumber || '-'
                  const extraPhones = uniquePhones.length - 1

                  const allEmails =
                    calls.length > 0
                      ? calls.map((c) => c.email?.trim()).filter(Boolean)
                      : dcr.email
                        ? [dcr.email.trim()]
                        : []
                  const uniqueEmails = [...new Set(allEmails)]
                  const primaryEmail = uniqueEmails[0] || dcr.email || '-'
                  const extraEmails = uniqueEmails.length - 1

                  return (
                    <tr key={dcr._id} className="border-b last:border-b-0">
                      <td className="px-5 py-4 text-sm font-medium text-slate-700 whitespace-nowrap">
                        {dcr.date
                          ? new Date(dcr.date).toLocaleDateString('en-GB')
                          : dcr.createdAt
                            ? new Date(dcr.createdAt).toLocaleDateString(
                                'en-GB',
                              )
                            : '-'}
                      </td>

                      <td className="px-5 py-4 text-sm font-semibold text-slate-900">
                        <div className="flex items-center gap-1.5 flex-wrap">
                          <span>{primaryCompany}</span>
                          {extraCompanies > 0 && (
                            <button
                              type="button"
                              onClick={() => handleOpenView(dcr)}
                              className="inline-flex items-center rounded-full bg-slate-100 px-2 py-0.5 text-xs font-semibold text-slate-600 transition hover:bg-slate-200"
                              title={uniqueCompanies.slice(1).join(', ')}
                            >
                              +{extraCompanies} more
                            </button>
                          )}
                        </div>
                      </td>

                      <td className="px-5 py-4 text-sm text-slate-700 whitespace-nowrap">
                        {dcr.createdBy?.name || dcr.createdByName || '-'}
                      </td>

                      <td className="px-5 py-4 text-sm text-slate-700">
                        <div className="flex items-center gap-1.5 flex-wrap">
                          <span>{primaryContact}</span>
                          {extraContacts > 0 && (
                            <button
                              type="button"
                              onClick={() => handleOpenView(dcr)}
                              className="inline-flex items-center rounded-full bg-slate-100 px-2 py-0.5 text-xs font-semibold text-slate-600 transition hover:bg-slate-200"
                              title={uniqueContacts.slice(1).join(', ')}
                            >
                              +{extraContacts} more
                            </button>
                          )}
                        </div>
                      </td>

                      <td className="px-5 py-4 text-sm text-slate-700 whitespace-nowrap">
                        <div className="flex items-center gap-1.5 flex-wrap">
                          <span>{primaryPhone}</span>
                          {extraPhones > 0 && (
                            <button
                              type="button"
                              onClick={() => handleOpenView(dcr)}
                              className="inline-flex items-center rounded-full bg-slate-100 px-2 py-0.5 text-xs font-semibold text-slate-600 transition hover:bg-slate-200"
                              title={uniquePhones.slice(1).join(', ')}
                            >
                              +{extraPhones} more
                            </button>
                          )}
                        </div>
                      </td>

                      <td className="px-5 py-4 text-sm text-slate-700">
                        <div className="flex items-center gap-1.5 flex-wrap">
                          <span
                            className="truncate max-w-[200px]"
                            title={primaryEmail}
                          >
                            {primaryEmail}
                          </span>
                          {extraEmails > 0 && (
                            <button
                              type="button"
                              onClick={() => handleOpenView(dcr)}
                              className="inline-flex shrink-0 items-center rounded-full bg-slate-100 px-2 py-0.5 text-xs font-semibold text-slate-600 transition hover:bg-slate-200"
                              title={uniqueEmails.slice(1).join(', ')}
                            >
                              +{extraEmails} more
                            </button>
                          )}
                        </div>
                      </td>

                      <td className="max-w-[260px] px-5 py-4 text-sm text-slate-700">
                        <div
                          className="truncate"
                          title={dcr.remarks || dcr.calls?.[0]?.remark || ''}
                        >
                          {dcr.remarks || dcr.calls?.[0]?.remark || '-'}
                        </div>
                      </td>

                      <td className="px-5 py-4">
                        <div className="flex items-center gap-2">
                          {/* View */}
                          <button
                            type="button"
                            onClick={() => handleOpenView(dcr)}
                            disabled={isSubmitting}
                            className="flex h-10 w-10 items-center justify-center rounded-full border border-slate-200 bg-slate-50 text-slate-600 transition hover:bg-slate-100 disabled:cursor-not-allowed disabled:opacity-50"
                            title="View DCR Details"
                          >
                            <Eye className="h-4 w-4" />
                          </button>

                          {/* Edit */}
                          <button
                            type="button"
                            onClick={() => handleOpenEdit(dcr)}
                            disabled={isSubmitting}
                            className="flex h-10 w-10 items-center justify-center rounded-full border border-slate-200 bg-slate-50 text-slate-600 transition hover:bg-slate-100 disabled:cursor-not-allowed disabled:opacity-50"
                            title="Edit DCR"
                          >
                            <Pencil className="h-4 w-4" />
                          </button>

                          {/* Delete */}
                          <button
                            type="button"
                            onClick={() => handleOpenDelete(dcr)}
                            disabled={isSubmitting}
                            className="flex h-10 w-10 items-center justify-center rounded-full border border-red-200 bg-red-50 text-red-500 transition hover:bg-red-100 disabled:cursor-not-allowed disabled:opacity-50"
                            title="Delete DCR"
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  )
                })
              )}
            </tbody>
          </table>
          <div className="flex items-center justify-between border-t px-5 py-4">
            <p className="text-sm text-slate-500">
              Showing{' '}
              <span className="font-medium text-slate-700">
                {dcrs.length
                  ? `${(currentPage - 1) * pageSize + 1}–${Math.min(
                      currentPage * pageSize,
                      dcrs.length,
                    )}`
                  : '0–0'}
              </span>{' '}
              of{' '}
              <span className="font-medium text-slate-700">{dcrs.length}</span>
            </p>

            <div className="flex items-center gap-2">
              <button
                type="button"
                disabled={currentPage === 1}
                onClick={() => setCurrentPage((page) => page - 1)}
                className="rounded-full border px-4 py-2 text-sm text-slate-700 disabled:cursor-not-allowed disabled:text-slate-400"
              >
                Previous
              </button>

              {Array.from(
                { length: Math.ceil(dcrs.length / pageSize) },
                (_, index) => index + 1,
              ).map((page) => (
                <button
                  key={page}
                  type="button"
                  onClick={() => setCurrentPage(page)}
                  className={`h-10 min-w-10 rounded-full px-3 text-sm font-medium ${
                    currentPage === page
                      ? 'bg-slate-950 text-white'
                      : 'border bg-white text-slate-700 hover:bg-slate-50'
                  }`}
                >
                  {page}
                </button>
              ))}

              <button
                type="button"
                disabled={currentPage >= Math.ceil(dcrs.length / pageSize)}
                onClick={() => setCurrentPage((page) => page + 1)}
                className="rounded-full border px-4 py-2 text-sm text-slate-700 disabled:cursor-not-allowed disabled:text-slate-400"
              >
                Next
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Calls Details Modal */}
      {selectedViewDCR && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4 backdrop-blur-xs"
          onClick={handleCloseView}
        >
          <div
            className="w-full max-w-4xl overflow-hidden rounded-2xl bg-white shadow-2xl transition-all"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="flex items-center justify-between border-b px-6 py-5">
              <div>
                <h2 className="text-xl font-bold text-slate-900">
                  DCR Call Details
                </h2>

                <p className="mt-1 text-xs text-slate-500">
                  {selectedViewDCR.customerName ||
                    selectedViewDCR.createdBy?.name ||
                    'Customer'}{' '}
                  • Visit Date:{' '}
                  {selectedViewDCR.date
                    ? new Date(selectedViewDCR.date).toLocaleDateString('en-GB')
                    : selectedViewDCR.createdAt
                      ? new Date(selectedViewDCR.createdAt).toLocaleDateString(
                          'en-GB',
                        )
                      : '-'}{' '}
                  • {selectedViewDCR.calls?.length || 0} Total Calls
                </p>
              </div>

              <button
                type="button"
                onClick={handleCloseView}
                className="flex h-9 w-9 items-center justify-center rounded-full text-slate-500 transition hover:bg-slate-100 hover:text-slate-900"
                aria-label="Close"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            {/* Tabular Form */}
            <div className="p-6">
              {selectedViewDCR.calls?.length > 0 ? (
                <div className="overflow-hidden rounded-xl border border-slate-200">
                  <div className="overflow-x-auto">
                    <table className="w-full min-w-[700px]">
                      <thead>
                        <tr className="border-b bg-slate-50">
                          <th className="w-[85px] px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-slate-600">
                            Call
                          </th>
                          <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-slate-600">
                            Company
                          </th>
                          <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-slate-600">
                            Contact Person
                          </th>
                          <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-slate-600">
                            Phone
                          </th>
                          <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-slate-600">
                            Email
                          </th>
                          <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-slate-600">
                            Remark
                          </th>
                        </tr>
                      </thead>

                      <tbody className="divide-y divide-slate-100">
                        {selectedViewDCR.calls.map((call, index) => (
                          <tr
                            key={`${selectedViewDCR._id}-call-${index}`}
                            className="transition hover:bg-slate-50/60"
                          >
                            <td className="px-4 py-3.5 text-sm font-bold text-slate-900 whitespace-nowrap">
                              Call {call.callNumber || index + 1}
                            </td>

                            <td className="px-4 py-3.5 text-sm font-semibold text-slate-900">
                              {call.customerName ||
                                selectedViewDCR.customerName ||
                                '-'}
                            </td>

                            <td className="px-4 py-3.5 text-sm text-slate-700">
                              {call.contactPersonName ||
                                selectedViewDCR.contactPersonName ||
                                '-'}
                            </td>

                            <td className="px-4 py-3.5 text-sm text-slate-700 whitespace-nowrap">
                              {call.phoneNumber ||
                                selectedViewDCR.phoneNumber ||
                                '-'}
                            </td>

                            <td className="px-4 py-3.5 text-sm text-slate-700 break-all">
                              {call.email || selectedViewDCR.email || '-'}
                            </td>

                            <td className="px-4 py-3.5 text-sm leading-6 text-slate-600">
                              {call.remark || '-'}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              ) : (
                <div className="rounded-xl border border-dashed border-slate-200 px-4 py-8 text-center">
                  <p className="text-sm text-slate-400">
                    No call details available
                  </p>
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="flex justify-end border-t bg-slate-50/50 px-6 py-4">
              <button
                type="button"
                onClick={handleCloseView}
                className="rounded-full bg-slate-950 px-6 py-2 text-sm font-medium text-white transition hover:bg-slate-800"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      <EditDCRModal
        key={selectedDCR?._id}
        open={isEditOpen}
        onOpenChange={(open) => {
          setIsEditOpen(open)

          if (!open) {
            setSelectedDCR(null)
          }
        }}
        dcr={selectedDCR}
        onSubmit={updateDCR}
        isSubmitting={isSubmitting}
      />

      <ConfirmDialog
        open={isDeleteOpen}
        onOpenChange={setIsDeleteOpen}
        title="Delete DCR"
        description={`Are you sure you want to delete the DCR for "${dcrToDelete?.customerName}"? This action cannot be undone.`}
        confirmText="Delete"
        loading={isDeleting}
        onConfirm={handleConfirmDelete}
      />
    </div>
  )
}
